/**
 * Copyright (C) Radiojar S.A. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 *
 * Created by Tasos Kolokathis on 2/2/23.
 */

import AstrumProxyApi from "@/services/AstrumProxy";
import WebRTCService from "@/services/WebRTCService";
import ConferenceRoles from "@/enums/ConferenceRoles";

export default {
	state: {
		webRTCToken: null,

		webRTCStream: null,

		signalerConnected: false,

		micOn: null,

		usersMicState: [],

		usersAirState: [],

		nebulaAdapterOpen: false
	},

	getters: {
		getWebRTCToken: (state) => () => {
			return state.webRTCToken;
		},

		getWebRTCPlayerKey: (state, getters) => () => {
			const accountId = getters.getOrgID();
			const stationId = getters.liveModeSelectedStationID();

			return `${accountId}.${stationId}`;
		},

		getWebRTCStream: (state) => () => {
			return state.webRTCStream;
		},

		isMicOn: (state) => () => {
			return state.micOn;
		},

		isUserMicOn: (state) => (user) => {
			return state.usersMicState.find(obj => {
				return obj.user === user
			})?.on;
		},

		isUserOnAir: (state) => (user) => {
			return state.usersAirState.find(obj => {
				return obj.user === user
			})?.on;
		},

		isSignalerConnected: (state) => () => {
			return state.signalerConnected;
		},

		isNebulaAdapterOpen: (state) => () => {
			return state.nebulaAdapterOpen;
		}
	},

	mutations: {
		setWebRTCToken(state, token) {
			state.webRTCToken = token;
		},

		setWebRTCStream(state, stream) {
			state.webRTCStream = stream;
		},

		setSignalerConnectionStatus(state, isConnected) {
			state.signalerConnected = isConnected;
		},

		setNebulaAdapterStatus(state, isOpen) {
			state.nebulaAdapterOpen = isOpen;
		},

		setMicOnOff(state, micOn) {
			state.micOn = micOn;
		},

		setUserMicState(state, playFlipped) {
			let user = state.usersMicState.find(obj => {
				return obj.user === playFlipped.user;
			});

			if (user) {
				user.on = playFlipped.on;
			}
			else {
				state.usersMicState.push({
					user: playFlipped.user,
					on: playFlipped.on
				})
			}
		},

		setUserAirState(state, airFlipped) {
			let user = state.usersAirState.find(obj => {
				return obj.user === airFlipped.user;
			});

			if (user) {
				user.on = airFlipped.on;
			}
			else {
				state.usersAirState.push({
					user: airFlipped.user,
					on: airFlipped.on
				})
			}
		}
	},

	actions: {
		getWebRTCToken({getters, commit}) {
			const accountId = getters.liveModeSelectedStation().OrganizationID;
			const stationId = getters.liveModeSelectedStationID();
			const playerKey = getters.getWebRTCPlayerKey();

			return AstrumProxyApi.getAstrumPlayerToken(accountId, stationId, playerKey)
								 .then(response => {
									 commit("setWebRTCToken", response.data.token);
								 })
			                     .catch(() => {
									 commit("setWebRTCToken", null);
									 commit(
										 "SHOW_NOTIFICATION_WITH_TYPE",
										 {message: "Could not authorise a connection to your microphone."}
									 );
									 return Promise.reject(new Error("Could not authorise WebRTC token"));
								 });
		},

		getWebRTCGuestToken({getters, commit}, guestID) {
			const accountId = getters.liveModeSelectedStation().OrganizationID;
			const stationId = getters.liveModeSelectedStationID();
			const playerKey = getters.getWebRTCPlayerKey();
			const payload = {guest_id: guestID};

			return AstrumProxyApi.getAstrumPlayerGuestToken(accountId, stationId, playerKey, payload)
								 .then(response => response.data.token)
								 .catch(() => {
									 commit(
										 "SHOW_NOTIFICATION_WITH_TYPE",
										 {message: "Could not generate a guest WebRTC token."}
									 );
									 return Promise.reject(new Error("Could not generate a guest WebRTC token"));
								 });
		},

		openWebRTCConnection({getters, commit}, role) {
			if (getters.isWebRTCStudio() && getters.getWebRTCToken()) {
				WebRTCService.initialize();

				if (role !== ConferenceRoles.Guest) {
					let selectedDeviceId = getters.getActiveStudioInputDevices()[0]?.device ?? "default";

					return navigator.mediaDevices.getUserMedia({
										video: false,
										audio: {
											...((selectedDeviceId && selectedDeviceId !== "default") &&
												{deviceId: {exact: selectedDeviceId}}),
											channelCount: 2,
											autoGainControl: false,
											echoCancellation: false,
											noiseSuppression: false
										}
									})
					                .then((stream) => {
										// Do this so that live state has priority over auto_enable_mic
										if (!JSON.parse(sessionStorage.getItem("liveState"))) {
											commit("setMicOnOff", getters.getStudioSettings().auto_enable_mic);
										}
										else {
											sessionStorage.removeItem("liveState");
										}
										return WebRTCService.openConnection(role)
						                                    .then(() => {
																WebRTCService.connectOutputSource({
																	id: selectedDeviceId, stream
																});
															});
									})
					                .catch((error) => {
										console.error("getUserMedia error: ", error);
										return Promise.reject(error);
									});
				}
				else {
					return WebRTCService.openConnection(role);
				}
			}
			else {
				return Promise.reject(new Error("WebRTC studio not authorized or token missing"));
			}
		},

		setSignalerOutputStreamSource({getters}, {device, oldDevice}) {
			if (getters.isSignalerConnected() && getters.getWebRTCStream()) {
				const oldSelectedDeviceId = oldDevice?.device ?? "default";
				const selectedDeviceId = device?.device ?? "default";

				navigator.mediaDevices.getUserMedia({
							 video: false,
							 audio: selectedDeviceId !== "default" ? {deviceId: {exact: selectedDeviceId}} : true
						 })
				         .then((stream) => {
							 WebRTCService.disconnectOutputSource(oldSelectedDeviceId);
							 WebRTCService.connectOutputSource({id: selectedDeviceId, stream});
						 })
				         .catch((error) => {
							 console.error("getUserMedia error: ", error);
						 });
			}
		},

		addSignalerOutputSource({getters}, source) {
			if (getters.isSignalerConnected() && getters.getWebRTCStream()) {
				WebRTCService.connectOutputSource(source);
			}
		},

		removeSignalerOutputSource({getters}, source) {
			if (getters.isSignalerConnected() && getters.getWebRTCStream()) {
				WebRTCService.disconnectOutputSource(source);
			}
		},

		toggleUserMic(state, settings = {}) {
			WebRTCService.flipPlay(settings.on, settings.user);
		},

		toggleUserAirState(state, settings) {
			WebRTCService.flipAir(settings.on, settings.user);
		},

		playPot(state, settings) {
			WebRTCService.flipChannel(settings.state, settings.channel);
		},

		setGainMaster(state, gain) {
			WebRTCService.setMasterGain(gain);
		},

		setGainChannel(state, settings) {
			WebRTCService.setChannelGain(settings.gain, settings.channel);
		},

		setGainMic(state, settings) {
			WebRTCService.setGain(settings.gain, settings.user);
		},

		adjustDucking(state, threshold) {
			WebRTCService.adjustDucking(threshold);
		},

		reopenWebRTCConnection(state, role) {
			if (WebRTCService.signaler) {
				WebRTCService.closeConnection(false);
				WebRTCService.openConnection(role);
			}
		},

		closeWebRTCConnection({commit}, reset) {
			if (WebRTCService.signaler) {
				WebRTCService.closeConnection();
			}

			if (reset) {
				commit("setWebRTCToken", null);
			}
		}
	}
};
